import {batch} from 'react-redux';

import _ from 'lodash';
import {setDefaultTipTouchOffDirectionTasks} from 'lib/PipettingProfileItemHelper';
import {validateMinVolumeAllowed} from 'lib/protocolHelper';
import {filterToArray, find, filter, selectDistinct} from '../../lib/objectHelper';
import {
  getMinTipPositionByLabwareId,
  restoreTipCountForCards,
  restoreTipCountForSingleCard,
} from './protocolManagementBusiness';
import {saveState} from './stateBusiness';

import * as taskListBusinessAction from './taskListBusinessAction';
import {convertRowToAlphabet, generateCardIDAndTaskIDAndNumber, getNewCardName} from './commonTaskListBusiness';
import * as cardStateAction from '../actions/cardStateAction';
import * as undoRedoBusinessAction from './undoRedoBusinessAction';
import * as cardPoolingStateAction from '../actions/cardPoolingStateAction';
import * as labwareStateAction from '../actions/labwareStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import jsonCopy from '../../lib/jsonCopy';

import {
  TaskType,
  CardType,
  WellCount,
  TipChannelType,
  SWIPING_DIRECTION_X,
  SWIPING_DIRECTION_Y,
  LabwareWellCount,
  TransformSelectionType,
  Feedback,
  TrayType,
  CardInvalidType,
  ProtocolEditorViewMode,
} from '../../constants/dataEnum';

import {
  sortTaskByWellPosition,
  generateRowColumnsListFromSwipingInfo,
  convertRowToNumeric,
  getTaskGroups,
  getTaskGroupByTipCountAndLabware,
  getQuadrant,
} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';

import {showSnackbar} from '../actions/appStateAction';

import * as protocolEditorStateAction from '../actions/protocolEditorAction';
import {resolveIntl} from './commonBusiness';
import {getGCDFromArray} from '../../lib/numericHelper';
import {extractAdditionalPickEjectTipsAndAdjustTask, isPoolingCardValid} from '../../lib/cardAndTaskHelper';
import {adjustTaskPipettingParametersByLabware, extractPipettingParameterFromTask} from './pipettingParameterBusiness';

const intl = resolveIntl();

export const checkInvalidPoolingCard = (cardId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const isCardValid = isPoolingCardValid(cards[cardId], tasks);

  if (isCardValid) {
    dispatch(cardStateAction.removeCardInvalidType(cardId));
  } else {
    dispatch(cardStateAction.addCardInvalidType(cardId, [CardInvalidType.UNFINISHED]));
  }
};

export const restorePoolingWizard = (showVolumeModal) => (dispatch, getState) => {
  const {cardState, labwareState} = getState();
  const {cards, tasks} = cardState;

  const unfinishedSdCard = Object.values(cards).find(
    (e) => e.type === CardType.POOLING && e.invalidTypes?.includes(CardInvalidType.UNFINISHED),
  );

  if (unfinishedSdCard) {
    const taskAspirate = Object.values(tasks).find(
      (e) => unfinishedSdCard?.tasks.includes(e.ID) && e.type === TaskType.ASPIRATE,
    );

    dispatch(protocolEditorStateAction.setSelectedMenu(ProtocolEditorViewMode.POOLING));
    if (taskAspirate?.volume === 0) {
      dispatch(protocolEditorStateAction.setPoolingTaskStep(2));
      const taskAspirateLabwareId = taskAspirate?.labwareID;
      const currentDeckNumber = Object.keys(labwareState).find(
        (e) => labwareState[e].LabwareID === taskAspirateLabwareId,
      );
      showVolumeModal(Number(currentDeckNumber));
    } else {
      dispatch(protocolEditorStateAction.setPoolingTaskStep(3));
    }
  }
};

export const getTaskAspiratePooling = (labwareId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const currentSelectedCard = find(cards, {isSelected: true});

  if (!currentSelectedCard) {
    return [];
  }

  const returnTasks = labwareId
    ? filterToArray(tasks, {ID: currentSelectedCard.tasks, labwareID: labwareId, type: TaskType.ASPIRATE})
    : filterToArray(tasks, {ID: currentSelectedCard.tasks, type: TaskType.ASPIRATE});

  return returnTasks;
};

export const getTaskDispensePooling = (labwareId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const currentSelectedCard = find(cards, {isSelected: true});

  if (!currentSelectedCard) {
    return [];
  }

  const returnTasks = labwareId
    ? filterToArray(tasks, {ID: currentSelectedCard.tasks, labwareID: labwareId, type: TaskType.DISPENSE})
    : filterToArray(tasks, {ID: currentSelectedCard.tasks, type: TaskType.DISPENSE});

  return returnTasks;
};

export const setPoolingVolume =
  (volume, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState, configState, protocolState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const {tasks: selectedCardTasks} = selectedCard;

    const selectedTasksAspirate = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
    const selectedTasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});
    const selectedTasks = selectedTasksAspirate.concat(selectedTasksDispense);

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const maxTipVolume =
      protocolTrayType === TrayType.HIGH_VOLUME_CADDY
        ? configState.maxAspirateHighVolume.value
        : configState.maxAspirateVolume.value;

    const taskVolume = parseFloat(parseFloat(volume).toFixed(1));

    const paramTasks = selectedTasks.map((e) => {
      let mixingVolume =
        // eslint-disable-next-line no-nested-ternary
        e.mixingVolume ? e.mixingVolume : taskVolume;

      mixingVolume = parseFloat(mixingVolume);
      mixingVolume = mixingVolume > maxTipVolume ? maxTipVolume : mixingVolume;
      const {validatedVolume} = dispatch(validateMinVolumeAllowed(taskVolume));
      const {validatedVolume: validatedMixingVolume} = dispatch(validateMinVolumeAllowed(mixingVolume));

      return {
        id: e.ID,
        volume: validatedVolume,
        mixingVolume: validatedMixingVolume,
      };
    });

    batch(() => {
      dispatch(cardPoolingStateAction.setPoolingVolume(paramTasks));
      dispatch(restoreTipCountForCards([selectedCard.ID]));

      const hasEjectTipTask = find(tasks, {ID: selectedCardTasks, type: TaskType.EJECT_TIP});

      if (hasEjectTipTask) {
        if (!skipSaveState) {
          dispatch(saveState());
        }

        return;
      }

      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const poolingBackHandler =
  (currentStep, isDelete = false) =>
  async (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const {ID: selectedCardId, tasks: selectedCardTasks} = selectedCard;

    const selectedTasksAspirate = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
    const selectedTasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});
    const selectedTasks = selectedTasksAspirate.concat(selectedTasksDispense);

    const taskDispenses = selectedTasks.filter((e) => e.type === TaskType.DISPENSE);

    if (currentStep === 2 || currentStep === 1) {
      dispatch(taskListBusinessAction.deleteCard(selectedCardId));

      return;
    }

    if (currentStep === 3 && isDelete) {
      dispatch(setPoolingVolume(0));
      return;
    }

    const taskDispenseIds = taskDispenses.map((e) => e.ID);

    dispatch(cardStateAction.removeCardTasks(selectedCardId, taskDispenseIds));
    dispatch(saveState());
  };

export const cancelHandler = () => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  const {ID: selectedCardId} = selectedCard;

  dispatch(taskListBusinessAction.deleteCard(selectedCardId));
};

const createPoolingCard = (cardId) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const card = cards[cardId];

  if (card) {
    return;
  }

  const cardName = getNewCardName(cards, CardType.POOLING);

  await dispatch(cardPoolingStateAction.addPoolingCard(cardId, cardName, CardType.POOLING, false));
};

const generateNewPoolingTask =
  (
    taskId,
    number,
    type,
    // eslint-disable-next-line default-param-last
    labwareId = 0,
    // eslint-disable-next-line default-param-last
    row = '',
    // eslint-disable-next-line default-param-last
    column = '',
    // eslint-disable-next-line default-param-last
    rowTipCount = 1,
    // eslint-disable-next-line default-param-last
    columnTipCount = 1,
    existingAspirateTask,
  ) =>
  (dispatch, getState) => {
    const {getWellTarget} = taskListBusinessAction;

    const {cardState, labwareState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});
    const selectedLabware = find(labwareState, {LabwareID: labwareId});
    /* eslint-disable-next-line no-nested-ternary */
    const selectedTasks = existingAspirateTask
      ? {}
      : !Object.values(tasks).length || !selectedCard
        ? {}
        : filter(tasks, {ID: selectedCard.tasks});

    let startRow = row;
    let startColumn = column;

    if (selectedLabware) {
      const {rowCount: labwareRowCount, colCount: labwareColCount} = selectedLabware;

      const labwareWellCount = labwareColCount * labwareRowCount;
      const is384 = labwareWellCount === WellCount.WELL_384;

      let skipWellRow = 0;
      let skipWellColumn = 0;

      if (is384) {
        skipWellRow = 1;
        skipWellColumn = 1;
      }

      const rowTipCountNeeded = skipWellRow === 0 ? rowTipCount : rowTipCount + (rowTipCount - 1) * skipWellRow;
      const columnTipCountNeeded =
        skipWellColumn === 0 ? columnTipCount : columnTipCount + (columnTipCount - 1) * skipWellColumn;

      if (startRow + rowTipCountNeeded > labwareRowCount && !selectedLabware?.isTrough) {
        startRow = labwareRowCount - rowTipCountNeeded + 1;
      }

      if (startColumn + columnTipCountNeeded > labwareColCount && !selectedLabware?.isTrough) {
        startColumn = labwareColCount - columnTipCountNeeded + 1;
      }

      if (is384) {
        if (rowTipCount === 8) {
          startRow = startRow >= 2 ? 2 : 1;
        } else if (columnTipCount === 12) {
          startColumn = startColumn >= 2 ? 2 : 1;
        }
      }
    }

    // set default config
    const minTipPosition = dispatch(getMinTipPositionByLabwareId(labwareId));

    let task = {
      ID: taskId,
      number,
      type,
      labwareID: labwareId,
      row: startRow,
      column: startColumn,
      volume: 0,
      rowTipCount,
      columnTipCount,
      enableMixing: false,
      mixingCycle: 3,
      mixingVolume: 0,
      tipPosition: minTipPosition,
      mixingPositionDispense: minTipPosition,
      mixingPositionAspirate: minTipPosition,
      speed: 3,
      retractSpeed: 20,
      mixingSpeed: 3,
      isIgnored: false,
      tipTouchOff: false,
      tipTouchOffDelay: 0,
      tipTouchOffHeight: selectedLabware?.wellDepth,
    };

    task = setDefaultTipTouchOffDirectionTasks(selectedLabware, task);

    let existingPipettingParam = {};

    if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
      const [alphabetRow, numericColumn] = dispatch(
        getWellTarget(task.labwareID, TipChannelType.TIP_CHANNEL_1, task.row, task.column),
      );

      const existingTask = existingAspirateTask || find(selectedTasks, {type: task.type});
      if (task.type === TaskType.DISPENSE) {
        // when not editing pooling tasks (existing tasks does not exists yet), use aspirate task to fill the volume and pipetting details
        const existDispenseTask =
          find(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE}) || existingAspirateTask;

        if (!_.isEmpty(existDispenseTask)) {
          existingPipettingParam = extractPipettingParameterFromTask(existDispenseTask);
        }
      } else if (!_.isEmpty(existingTask)) {
        existingPipettingParam = extractPipettingParameterFromTask(existingTask);
      }

      task = dispatch(
        adjustTaskPipettingParametersByLabware(
          {
            ...task,
            row: alphabetRow,
            column: numericColumn,
            ...existingPipettingParam,
          },
          selectedLabware,
        ),
      );
    }

    return task;
  };

export const batchAddPoolingTaskByCardId =
  (cardId, taskList = [], skipSaveState = false, isResetNumber = false, ignoreSelectedCard = false) =>
  (dispatch, getState) => {
    if (!taskList.length) {
      return;
    }

    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = !ignoreSelectedCard ? find(cards, {isSelected: true}) : find(cards, {ID: cardId});

    if (!selectedCard) {
      return;
    }

    const isReuseTip = typeof selectedCard?.reuseTips === 'boolean' ? selectedCard?.reuseTips : true;

    const selectedTasks = !Object.values(tasks).length || !selectedCard ? {} : filter(tasks, {ID: selectedCard.tasks});

    const existTaskPickTip = isReuseTip ? find(selectedTasks, {type: TaskType.PICK_TIP}) : null;
    const taskEjectTip = isReuseTip ? find(selectedTasks, {type: TaskType.EJECT_TIP}) : null;
    const existAspirateTask = find(selectedTasks, {type: TaskType.ASPIRATE}) || {};

    const newTaskList = [];

    const [, taskId, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.POOLING,
      selectedCard?.ID,
      taskList[0].type,
      null,
      {row: taskList[0].rowTipCount, column: taskList[0].columnTipCount},
    );

    let iterator = 0;
    const newNumber = isResetNumber ? iterator + 1 : number;

    taskList.forEach((taskItem, idx) => {
      const {type, labwareId, row, column, rowTipCount, columnTipCount} = taskItem;
      const volume = taskItem?.volume || existAspirateTask?.volume;
      const mixingVolume = taskItem?.mixingVolume || existAspirateTask?.mixingVolume;

      if ((!isReuseTip && type === TaskType.ASPIRATE) || (isReuseTip && !existTaskPickTip && idx === 0)) {
        const pickTipTask = {
          ID: taskId + iterator,
          type: TaskType.PICK_TIP,
          columnTipCount,
          rowTipCount,
          number: newNumber + iterator,
          labwareID: 2,
          isIgnored: taskItem.isIgnored ?? false,
        };
        newTaskList.push(pickTipTask);
        iterator += 1;
      }

      const task = dispatch(
        generateNewPoolingTask(
          taskId + iterator,
          newNumber + iterator,
          type,
          labwareId,
          row,
          column,
          rowTipCount,
          columnTipCount,
          existAspirateTask,
        ),
      );
      if (task.volume === 0) {
        task.volume = volume;
        task.mixingVolume = mixingVolume;
      }
      newTaskList.push(task);
      iterator += 1;

      if ((!isReuseTip && type === TaskType.DISPENSE) || (isReuseTip && !taskEjectTip && idx === taskList.length - 1)) {
        const ejectTipTask = {
          ID: taskId + iterator,
          type: TaskType.EJECT_TIP,
          columnTipCount,
          rowTipCount,
          number: newNumber + iterator,
          isIgnored: taskItem.isIgnored ?? false,
          labwareID: 0,
          changeTip: !isReuseTip,
        };
        newTaskList.push(ejectTipTask);
        iterator += 1;
      }
    });

    const cardName = cards[cardId]?.name || getNewCardName(cards, CardType.POOLING);

    const dispenseTask = newTaskList.find((task) => task.type === TaskType.DISPENSE);

    batch(() => {
      dispatch(cardStateAction.batchAddCardTask(cardId, cardName, CardType.POOLING, newTaskList));
      if (newTaskList[0].type === TaskType.DISPENSE || (newTaskList[0].type === TaskType.ASPIRATE && isReuseTip)) {
        const taskPickTip = find(selectedTasks, {type: TaskType.PICK_TIP});

        // sync pick tip task tip count
        if (
          taskPickTip.rowTipCount !== newTaskList[0].rowTipCount ||
          taskPickTip.columnTipCount !== newTaskList[0].columnTipCount
        ) {
          const newTaskPickTip = {
            ...taskPickTip,
            rowTipCount: newTaskList[0].rowTipCount,
            columnTipCount: newTaskList[0].columnTipCount,
          };

          dispatch(cardStateAction.restoreCardTaskStateById(newTaskPickTip));
        }
      }

      // make sure eject tip task is always last (only in reuse tip mode)
      if (taskEjectTip) {
        dispatch(cardStateAction.removeCardTask(selectedCard.ID, taskEjectTip.ID));

        const newTaskEjectTip = {
          ...taskEjectTip,
          ID: taskId + iterator + 1,
          rowTipCount: newTaskList[0].rowTipCount,
          columnTipCount: newTaskList[0].columnTipCount,
        };

        dispatch(
          cardStateAction.addCardTask(
            selectedCard.ID,
            selectedCard.name,
            CardType.POOLING,
            newTaskEjectTip.ID,
            newTaskEjectTip,
          ),
        );
      }

      if (dispenseTask) {
        dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition([dispenseTask.labwareID]));
      }

      dispatch(checkInvalidPoolingCard(cardId));

      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const convertPoolingTaskOnTrough =
  (row, column, destinationTask, sourceTasks, newLabware, selectedCard, maintainWellCount = false) =>
  (dispatch, getState) => {
    const {labwareState} = getState();
    const {rowCount, colCount, isTrough, LabwareID} = newLabware;
    const sampleTask = jsonCopy(destinationTask);
    let taskRowCount = 1;
    let taskColCount = 1;
    const isReuseTips = selectedCard?.reuseTips;
    const newTaskList = [];

    const isDestLabwareChanges = destinationTask.labwareID !== newLabware.LabwareID;
    let destTouchOffDirectionTask = {};
    if (isDestLabwareChanges) {
      destTouchOffDirectionTask = setDefaultTipTouchOffDirectionTasks(newLabware, destinationTask);
    }

    // remove previous tasks
    dispatch(cardStateAction.removeAllCardTask(selectedCard?.ID));

    let [, taskId, number] = generateCardIDAndTaskIDAndNumber(
      getState().cardState.cards,
      getState().cardState.tasks,
      CardType.POOLING,
      selectedCard?.ID,
      TaskType.EJECT_TIP,
      null,
    );

    sourceTasks.forEach((aspirateTask, idx) => {
      const sourceLabware = find(labwareState, {LabwareID: aspirateTask.labwareID});

      let newRowTipCount = sampleTask.rowTipCount > rowCount && !isTrough ? rowCount : sampleTask.rowTipCount;
      let newColTipCount = sampleTask.columnTipCount > colCount && !isTrough ? colCount : sampleTask.columnTipCount;

      const dispenseSize = {
        row: sampleTask.rowTipCount > rowCount && isTrough ? rowCount : newRowTipCount,
        column: sampleTask.columnTipCount > colCount && isTrough ? colCount : newColTipCount,
      };

      if (!maintainWellCount) {
        newRowTipCount = aspirateTask.rowTipCount;
        newColTipCount = aspirateTask.columnTipCount;

        if (newRowTipCount > 1 && rowCount > 1 && dispenseSize.row === 1) {
          taskRowCount = newRowTipCount;
          newRowTipCount = 1;
        }

        if (newColTipCount > 1 && colCount > 1 && dispenseSize.column === 1) {
          taskColCount = newColTipCount;
          newColTipCount = 1;
        }
      } else {
        if (newRowTipCount === 1) {
          taskRowCount = aspirateTask.rowTipCount;
        }

        if (newColTipCount === 1) {
          taskColCount = aspirateTask.columnTipCount;
        }
      }

      const aspirateRow = aspirateTask.row.charCodeAt(0) - 64;
      const aspirateColumn = aspirateTask.column;

      // pick tip

      const pickTipTask = {
        type: TaskType.PICK_TIP,
        rowTipCount: newRowTipCount,
        columnTipCount: newColTipCount,
        labwareID: 2,
      };

      const ejectTipTask = {
        type: TaskType.EJECT_TIP,
        rowTipCount: newRowTipCount,
        columnTipCount: newColTipCount,
        row: '',
        column: '',
        labwareID: 0,
        changeTip: !isReuseTips,
      };

      if (isReuseTips && idx === 0) {
        newTaskList.push({
          ...pickTipTask,
          ID: taskId++,
          number,
        });
      }

      const sourceLabwr = sourceLabware ?? find(labwareState, {LabwareID: aspirateTask.labwareID});
      // eslint-disable-next-line no-unsafe-optional-chaining
      const is384source = sourceLabwr?.colCount * sourceLabwr.rowCount === 384;
      let row384Shifter;
      let col384Shifter = 0;

      // Aspirate and dispense tasks
      for (let c = aspirateColumn; c < aspirateColumn + taskColCount; c += 1) {
        row384Shifter = 0;
        for (let r = aspirateRow; r < aspirateRow + taskRowCount; r += 1) {
          if (!isReuseTips) {
            newTaskList.push({
              ...pickTipTask,
              ID: taskId++,
              number,
            });
          }
          newTaskList.push({
            ...aspirateTask,
            row: convertRowToAlphabet(r + row384Shifter),
            column: c + col384Shifter,
            ID: taskId++,
            number: number++,
            rowTipCount: newRowTipCount,
            columnTipCount: newColTipCount,
          });
          newTaskList.push({
            ...sampleTask,
            row: convertRowToAlphabet(row),
            column,
            ID: taskId++,
            number: number++,
            rowTipCount: newRowTipCount,
            columnTipCount: newColTipCount,
            labwareID: LabwareID,
            tipTouchOffHeight: isDestLabwareChanges ? newLabware.wellDepth : sampleTask.tipTouchOffHeight,
            tipTouchOffDirectionX: isDestLabwareChanges
              ? destTouchOffDirectionTask.tipTouchOffDirectionX
              : sampleTask.tipTouchOffDirectionX,
            tipTouchOffDirectionY: isDestLabwareChanges
              ? destTouchOffDirectionTask.tipTouchOffDirectionY
              : sampleTask.tipTouchOffDirectionY,
          });
          if (!isReuseTips) {
            newTaskList.push({
              ...ejectTipTask,
              ID: taskId++,
              number: number++,
            });
            taskId += 1;
            number += 1;
          } else {
            taskId += 1;
            number += 1;
          }

          if (is384source) {
            row384Shifter += 1;
          }
        }

        if (is384source) {
          col384Shifter += 1;
        }
      }

      // eject tip
      if (isReuseTips && idx === sourceTasks.length - 1) {
        newTaskList.push({
          ...ejectTipTask,
          ID: taskId++,
          number: number++,
        });
      }
    });

    batch(() => {
      dispatch(cardStateAction.batchAddCardTask(selectedCard.ID, selectedCard.name, CardType.POOLING, newTaskList));
      dispatch(checkInvalidPoolingCard(selectedCard.ID));
    });

    return newTaskList;
  };

export const addPoolingDispenseTasks =
  (labwareId = 0, row = 1, column = 1, rowTipCount = 1, columnTipCount = 1) =>
  async (dispatch, getState) => {
    batch(() => {
      const {cardState, labwareState} = getState();
      const {cards, tasks} = cardState;
      const selectedCard = find(cards, {isSelected: true});
      const {poolingHelperState} = selectedCard;
      const {groupedByColumn} = poolingHelperState;

      const labware = find(labwareState, {LabwareID: labwareId});

      if (labware.isTrough) {
        let taskGroups = [];
        // labware 12
        if (labware?.colCount > 1 && groupedByColumn && columnTipCount === 1) {
          const aspirateTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE}));
          taskGroups = getTaskGroups(aspirateTasks, labwareState, false);
          // labware 8
        } else if (labware?.rowCount > 1 && !groupedByColumn && rowTipCount === 1) {
          const aspirateTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE}));
          taskGroups = getTaskGroups(aspirateTasks, labwareState, true);
          // labware 1
        } else if (labware?.colCount === 1 && labware?.rowCount === 1 && rowTipCount === 1) {
          const aspirateTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE}));
          const columnBasedGroup = getTaskGroups(
            aspirateTasks,
            labwareState,
            true,
            {row: false, column: false},
            {row: 1, column: 1},
          );
          const rowBasedGroup = getTaskGroups(
            aspirateTasks,
            labwareState,
            false,
            {row: false, column: false},
            {row: 1, column: 1},
          );
          taskGroups = columnBasedGroup.length > rowBasedGroup.length ? rowBasedGroup : columnBasedGroup;
        } else {
          taskGroups = poolingHelperState.taskGroups;
        }

        const sampleTask = find(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
        const destinationTask = dispatch(
          generateNewPoolingTask(
            0,
            0,
            TaskType.DISPENSE,
            labwareId,
            row,
            column,
            rowTipCount,
            columnTipCount,
            sampleTask,
          ),
        );

        if (!destinationTask.volume) {
          destinationTask.volume = sampleTask.volume;
        }

        destinationTask.rowTipCount = rowTipCount;
        destinationTask.columnTipCount = columnTipCount;

        const sampleTaskList = [];

        taskGroups.forEach((group) => {
          const newSampleTask = {...sampleTask};
          newSampleTask.row = group.row;
          newSampleTask.column = group.column;
          newSampleTask.rowTipCount = group.rowTipCount;
          newSampleTask.columnTipCount = group.columnTipCount;
          newSampleTask.labwareID = group.labwareId;

          sampleTaskList.push(newSampleTask);
        });
        dispatch(
          convertPoolingTaskOnTrough(
            row,
            column,
            destinationTask,
            sampleTaskList,
            labware,
            selectedCard,
            undefined,
            false,
          ),
        );
        return;
      }

      let allAspirateTasks = [];
      if (!labware?.isTrough && rowTipCount === 1 && columnTipCount === 1) {
        allAspirateTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE}));
        const currentTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks}));
        const taskIds = currentTasks.map((task) => task.ID);
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, taskIds));
      } else if (!labware?.isTrough && (rowTipCount > 1 || columnTipCount > 1)) {
        const {taskGroups} = poolingHelperState;
        const sampleTask = find(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
        const isDispenseLabware384 = labware.rowCount * labware.colCount === WellCount.WELL_384;
        taskGroups.forEach((group) => {
          const aspirateLabware = labwareState[group.deckId];
          const is384 = aspirateLabware.rowCount * aspirateLabware.colCount === WellCount.WELL_384 ? 1 : 0;

          const normalizeRowTipCount = isDispenseLabware384 ? Math.ceil(rowTipCount / 2) : rowTipCount;
          const normalizeColumnTipCount = isDispenseLabware384 ? Math.ceil(columnTipCount / 2) : columnTipCount;

          const rowBasedTaskCount = group.rowTipCount / normalizeRowTipCount;
          const columnBasedTaskCount = group.columnTipCount / normalizeColumnTipCount;

          for (let i = 0; i < columnBasedTaskCount; i += 1) {
            const r = convertRowToNumeric(group.row);
            for (let j = 0; j < rowBasedTaskCount; j += 1) {
              const newRow = convertRowToAlphabet(
                r + j * (rowTipCount + is384) + (normalizeRowTipCount > 1 ? j * is384 : 0),
              );
              const newColumn =
                group.column + (i * (columnTipCount + is384) + (normalizeColumnTipCount > 1 ? i * is384 : 0));
              allAspirateTasks.push({
                ...sampleTask,
                row: newRow,
                column: newColumn,
                rowTipCount: normalizeRowTipCount,
                columnTipCount: normalizeColumnTipCount,
                labwareID: group.labwareId,
              });
            }
          }
        });
        const currentTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks}));
        const taskIds = currentTasks.map((task) => task.ID);
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, taskIds));
      }

      const taskList = [];
      allAspirateTasks.forEach((task) => {
        taskList.push({
          ...task,
          labwareId: task.labwareID,
          row: convertRowToNumeric(task.row),
        });
        taskList.push({
          type: TaskType.DISPENSE,
          row,
          column,
          rowTipCount: task.rowTipCount,
          columnTipCount: task.columnTipCount,
          labwareId,
          volume: task.volume,
          mixingVolume: task.mixingVolume,
        });
      });

      dispatch(batchAddPoolingTaskByCardId(selectedCard.ID, taskList, true));
      dispatch(restoreTipCountForSingleCard(selectedCard.ID));
    });
  };

export const addPoolingAspirateTasks =
  (labwareId = 0, rowColumnList = [], rowTipCount = 1, columnTipCount = 1, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const taskList = [];
    const selectedCard = find(cards, {isSelected: true});

    const [cardId, taskId, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.POOLING,
      selectedCard?.ID,
      TaskType.ASPIRATE,
      null,
      {row: rowTipCount, column: columnTipCount},
    );

    const cardName = cards[cardId]?.name || getNewCardName(cards, CardType.POOLING);

    batch(() => {
      dispatch(createPoolingCard(cardId));

      if (!selectedCard) {
        // add pick tip task if card is not exist
        const pickTipTask = {
          ID: taskId,
          type: TaskType.PICK_TIP,
          columnTipCount,
          rowTipCount,
          number,
          labwareID: 2,
        };

        dispatch(cardStateAction.addCardTask(cardId, cardName, CardType.POOLING, taskId, pickTipTask));
      }

      for (let i = 0, ii = rowColumnList.length; i < ii; i += 1) {
        const r = rowColumnList[i];
        const {row, column} = r;

        // dispatch(addPoolingTask(TaskType.ASPIRATE, labwareId, row, column, rowTipCount, columnTipCount, true));
        taskList.push({
          type: TaskType.ASPIRATE,
          row,
          column,
          rowTipCount,
          columnTipCount,
          labwareId,
          volume: 0,
          mixingVolume: 0,
        });
      }

      dispatch(batchAddPoolingTaskByCardId(cardId, taskList, skipSaveState));
      dispatch(cardStateAction.removeCardTasks(cardId, [taskId]));
    });
  };

export const addPoolingAspirateDispenseTasks =
  (labwareId = 0, rowColumnList = [], rowTipCount = 1, columnTipCount = 1, dispenseTask = {}) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    const taskList = [];

    for (let i = 0, ii = rowColumnList.length; i < ii; i += 1) {
      const r = rowColumnList[i];
      const {row, column} = r;

      taskList.push({type: TaskType.ASPIRATE, labwareId, row, column, rowTipCount, columnTipCount});
      taskList.push({
        type: TaskType.DISPENSE,
        labwareId: dispenseTask.labwareID,
        row: dispenseTask.row.charCodeAt(0) - 64,
        column: dispenseTask.column,
        rowTipCount: rowTipCount ?? dispenseTask.rowTipCount,
        columnTipCount: columnTipCount ?? dispenseTask.columnTipCount,
      });
    }

    batch(() => {
      dispatch(batchAddPoolingTaskByCardId(selectedCard.ID, taskList, true));
    });
  };

export const editPoolingTask =
  (taskParamList, saveUndoRedo = true) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;
    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard || !taskParamList.length) {
      return;
    }

    const taskList = [];

    for (let i = 0; i < taskParamList.length; i += 1) {
      const task = taskParamList[i];
      if (task) {
        const {row, column} = task;

        if ((row < 1 && row !== '') || (column < 1 && column !== '')) {
          break;
        }

        const newRow = row ? convertRowToAlphabet(row) : row;

        const taskParam = {
          ...task,
          row: newRow,
        };

        taskList.push(taskParam);
      }
    }

    const dispenseLabwareIds = _.uniq(
      taskParamList.filter((task) => task.type === TaskType.DISPENSE).map((task) => task.labwareID),
    );

    batch(() => {
      dispatch(cardStateAction.batchRestoreCardTaskStateById(taskList));
      dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition(dispenseLabwareIds));
      if (saveUndoRedo) {
        dispatch(saveState());
      }
    });
  };

export const removeExcesPoolingTask =
  (aspirateTaskIds = [], saveUndoRedo = false, defaultCardId = null, removeCardAfterDelete = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {ID: defaultCardId}) ?? find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const {ID: selectedCardId} = selectedCard;
    const selectedTaskIds = selectedCard.tasks;

    const taskIdsToRemove = aspirateTaskIds;

    aspirateTaskIds.forEach((id) => {
      const dispenseTaskIndex = selectedTaskIds.indexOf(id) + 1;
      const dispenseTaskId = selectedTaskIds[dispenseTaskIndex];
      taskIdsToRemove.push(dispenseTaskId);

      if (!selectedCard.reuseTips) {
        const pickTipTaskIndex = selectedTaskIds.indexOf(id) - 1;
        const pickTipTaskId = selectedTaskIds[pickTipTaskIndex];
        taskIdsToRemove.push(pickTipTaskId);

        const ejectTipTaskIndex = selectedTaskIds.indexOf(id) + 2;
        const ejectTipTaskId = selectedTaskIds[ejectTipTaskIndex];
        taskIdsToRemove.push(ejectTipTaskId);
      }
    });

    batch(() => {
      dispatch(cardStateAction.removeCardTasks(selectedCardId, taskIdsToRemove));
      if (
        removeCardAfterDelete &&
        ((selectedCard.tasks.length === taskIdsToRemove.length && !selectedCard.reuseTips) ||
          (selectedCard.tasks.length === taskIdsToRemove.length + 2 && selectedCard.reuseTips))
      ) {
        dispatch(cardStateAction.removeCard(selectedCardId));
      }
      if (saveUndoRedo) {
        dispatch(saveState());
      }
    });
  };

export const batchRedefinePoolingAspirateTasks =
  (
    // eslint-disable-next-line default-param-last
    labwareId = 0,
    taskList,
    tipCount,
  ) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards, tasks} = cardState;
    const labware = find(labwareState, {LabwareID: labwareId});
    const {rowCount, colCount} = labware;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const aspirateTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
    const dispenseTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE});
    const dispenseTask = dispenseTasks[0];
    let taskCount = 0;
    const editedTasks = [];
    const excessTask = [];
    const newTask = [];

    const newRowTipCount = tipCount.row > rowCount ? rowCount : tipCount.row;
    const newColTipCount = tipCount.column > colCount ? colCount : tipCount.column;

    aspirateTasks.forEach((task) => {
      if (taskCount < taskList.length) {
        const dummy = {
          ...task,
          column: taskList[taskCount].column,
          row: taskList[taskCount].row,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
          labwareID: labwareId,
        };
        editedTasks.push(dummy);
      } else {
        excessTask.push(task.ID);
      }
      taskCount += 1;
    });

    for (let i = taskCount; i < taskList.length; i += 1) {
      newTask.push({row: taskList[i].row, column: taskList[i].column});
    }

    if (newRowTipCount !== dispenseTask?.rowTipCount || newColTipCount !== dispenseTask?.columnTipCount) {
      dispenseTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          row: task.row.charCodeAt(0) - 64,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });

      const pickTipsTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.PICK_TIP});
      pickTipsTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });

      const ejectTipsTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.EJECT_TIP});
      ejectTipsTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });
    }

    dispatch(editPoolingTask(editedTasks, !(excessTask?.length && excessTask.length > 0)));
    if (excessTask.length) {
      dispatch(removeExcesPoolingTask(excessTask, true));
    }
    if (newTask.length) {
      dispatch(addPoolingAspirateDispenseTasks(labwareId, newTask, tipCount.row, tipCount.column, dispenseTask));
    }
  };

export const batchMovePoolingAspirateTasks =
  (newRow = 1, newColumn = 1, labwareId = 0, originLabwareId = 0) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const labware = find(labwareState, {LabwareID: labwareId});
    const {colCount, rowCount} = labware;
    const is384 = colCount * rowCount === 384;

    let aspirateTasks = dispatch(getTaskAspiratePooling(originLabwareId));
    aspirateTasks = sortTaskByWellPosition(aspirateTasks, true);

    const rows = aspirateTasks.map((task) => task.row.charCodeAt(0) - 64);
    const columns = aspirateTasks.map((task) => task.column);

    const aspirateTaskRow = Math.min(...rows);
    const aspirateTaskColumn = Math.min(...columns);

    const rowDiff = aspirateTaskRow - newRow;
    const columnDiff = aspirateTaskColumn - newColumn;

    const editedTasks = [];
    const excessTask = [];

    const sampleTask = aspirateTasks[0];

    const originLabware = find(labwareState, {LabwareID: sampleTask?.labwareID});
    // eslint-disable-next-line no-unsafe-optional-chaining
    const isOrigin384 = originLabware?.colCount * originLabware?.rowCount === 384;

    const newRowTipCount = sampleTask.rowTipCount;
    const newColTipCount = sampleTask.columnTipCount;

    let rowIncrementer = is384 && !isOrigin384 ? 1 : 0;
    let colIncrementer = is384 && !isOrigin384 ? 1 : 0;

    let lastRow;
    let lastCol;

    const rowColumns = aspirateTasks.map((task) => ({row: task.row.charCodeAt(0) - 64, column: task.column}));
    const rightMostRow = rowColumns.reduce((prev, current) => (prev.row < current.row ? prev : current)).row;
    const topMostCol = rowColumns.reduce((prev, current) => (prev.column < current.column ? prev : current)).column;

    const columnList = [];
    const rowList = [];

    aspirateTasks.forEach((task) => {
      const rowNumber = task.row.charCodeAt(0) - 64;
      const columnNumber = task.column;

      let rightMostRowDiff = Math.abs(rightMostRow - rowNumber);
      let topMostColDiff = Math.abs(topMostCol - columnNumber);

      if (isOrigin384 && !is384) {
        rightMostRowDiff /= 2;
        topMostColDiff /= 2;
      }

      if (lastRow !== rowNumber) {
        lastRow = rowNumber;
        if (rowNumber === rightMostRow && (is384 || isOrigin384)) {
          rowIncrementer = 0;
        } else {
          // eslint-disable-next-line no-nested-ternary
          rowIncrementer = (is384 && !isOrigin384 ? 1 : isOrigin384 && !is384 ? -1 : 0) * rightMostRowDiff;
        }
      }

      if (lastCol !== columnNumber) {
        lastCol = columnNumber;
        if (columnNumber === topMostCol && (is384 || isOrigin384)) {
          colIncrementer = 0;
        } else {
          // eslint-disable-next-line no-nested-ternary
          colIncrementer = (is384 && !isOrigin384 ? 1 : isOrigin384 && !is384 ? -1 : 0) * topMostColDiff;
        }
      }

      const newTaskColumn = colCount > 1 ? columnNumber - columnDiff + colIncrementer : 1;
      const newTaskRow = rowCount > 1 ? rowNumber - rowDiff + rowIncrementer : 1;

      if (
        newTaskColumn <= colCount &&
        newTaskRow <= rowCount &&
        !((colCount === 1 && rowList.includes(newTaskRow)) || (rowCount === 1 && columnList.includes(newTaskColumn)))
      ) {
        const dummy = {
          ...task,
          column: newTaskColumn,
          row: newTaskRow,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
          labwareID: labwareId,
        };

        editedTasks.push(dummy);
      } else {
        excessTask.push(task.ID);
      }

      if (colCount === 1) {
        rowList.push(newTaskRow);
      }
      if (rowCount === 1) {
        columnList.push(newTaskColumn);
      }
    });

    batch(() => {
      dispatch(editPoolingTask(editedTasks, !(excessTask?.length && excessTask.length > 0)));
      if (excessTask.length) {
        dispatch(removeExcesPoolingTask(excessTask, true));
      }

      dispatch(restoreTipCountForSingleCard(selectedCard.ID));
    });
  };

const getMaintailWellStatus = (
  oriColCount,
  oriRowCount,
  columnTipCount,
  rowTipCount,
  curColCount,
  curRowCount,
  newTipCount,
) => {
  const labwareSize = curColCount * curRowCount;
  if (rowTipCount === 1 && columnTipCount > 1 && oriColCount > 1 && newTipCount.row === 1 && newTipCount.column > 1) {
    if (labwareSize !== 8 && labwareSize !== 1) {
      return true;
    }
  }

  if (rowTipCount > 1 && columnTipCount === 1 && oriRowCount > 1 && newTipCount.row > 1 && newTipCount.column === 1) {
    if (labwareSize !== 12 && labwareSize !== 1) {
      return true;
    }
  }
  return false;
};

const getIsNeedToResetTask = (originLabware, currentLabware, prevLabware, dispenseTask, newTipCount) => {
  const {colCount: originColCount, rowCount: originRowCount} = originLabware;

  const {colCount: currentColCount, rowCount: currentRowCount} = currentLabware;
  let currentWellCount = currentRowCount * currentColCount;
  if (currentWellCount === 384) {
    currentWellCount = 96;
  }

  const {colCount: prevColCount, rowCount: prevRowCount, isTrough: prevIsTrough} = prevLabware;
  let prevWellCount = prevRowCount * prevColCount;
  if (prevWellCount === 384) {
    prevWellCount = 96;
  }

  const maintainWellCount = getMaintailWellStatus(
    originColCount,
    originRowCount,
    dispenseTask.columnTipCount,
    dispenseTask.rowTipCount,
    currentRowCount,
    currentColCount,
    newTipCount ?? {row: dispenseTask.rowTipCount, column: dispenseTask.columnTipCount},
  );

  const dispenseSizeRow = dispenseTask.rowTipCount > prevRowCount ? 1 : dispenseTask.rowTipCount;
  const dispenseSizeColumn = dispenseTask.columnTipCount > prevColCount ? 1 : dispenseTask.columnTipCount;

  if (newTipCount && (dispenseSizeRow !== newTipCount?.row || dispenseSizeColumn !== newTipCount?.column)) {
    return [true, maintainWellCount];
  }

  if (prevIsTrough && prevWellCount !== currentWellCount) {
    return [true, maintainWellCount];
  }

  return [prevWellCount !== currentWellCount, maintainWellCount];
};

export const batchEditPoolingDispenseTasks =
  (
    // eslint-disable-next-line default-param-last
    newRow = 1,
    // eslint-disable-next-line default-param-last
    newColumn = 1,
    // eslint-disable-next-line default-param-last
    labwareId = 0,
    newDispenseSize,
    originLabware,
    originDispenseTasks,
    originAspirateTasks,
    isMaintainWellCount = false,
  ) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards, tasks} = cardState;
    const labware = find(labwareState, {LabwareID: labwareId});

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    let dispenseTasks = jsonCopy(originDispenseTasks);
    const aspirateTasks = jsonCopy(originAspirateTasks);
    const oldDispenseTasks = dispatch(getTaskDispensePooling());

    const prevDispenseTask = find(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE});
    const previousLabware = find(labwareState, {LabwareID: prevDispenseTask.labwareID});

    const isLabware384 = labware.rowCount * labware.colCount === 384;
    const dispenseSize = {
      row: isLabware384 ? (newDispenseSize.row + 1) / 2 : newDispenseSize.row,
      column: isLabware384 ? (newDispenseSize.column + 1) / 2 : newDispenseSize.column,
    };

    const [resetTasks] = getIsNeedToResetTask(
      originLabware,
      labware,
      previousLabware,
      oldDispenseTasks[0],
      dispenseSize,
    );

    const editedTasks = [];

    if (resetTasks) {
      const {poolingHelperState} = selectedCard;
      const {allowedTips} = poolingHelperState;
      const maxSizePerGroup = {
        row: originLabware.rowCount === 1 && originLabware.colCount > 1 ? 1 : allowedTips.row,
        column: originLabware.rowCount > 1 && originLabware.colCount === 1 ? 1 : allowedTips.column,
      };
      let taskGroups = [];
      if (labware?.colCount > 1 && labware?.rowCount > 1) {
        // labware 96/384
        taskGroups = getTaskGroups(
          aspirateTasks,
          labwareState,
          false,
          {row: false, column: false},
          {row: 1, column: 1},
          maxSizePerGroup,
        );
      } else if (labware?.colCount > 1) {
        // labware 12
        taskGroups = getTaskGroups(
          aspirateTasks,
          labwareState,
          false,
          {row: false, column: false},
          {row: 1, column: 1},
          {row: null, column: maxSizePerGroup.column},
        );
      } else if (labware?.rowCount > 1) {
        // labware 8
        taskGroups = getTaskGroups(
          aspirateTasks,
          labwareState,
          true,
          {row: false, column: false},
          {row: 1, column: 1},
          {row: maxSizePerGroup.row, column: null},
        );
      } else {
        // labware 1
        const columnBasedGroup = getTaskGroups(
          aspirateTasks,
          labwareState,
          true,
          {row: false, column: false},
          {row: 1, column: 1},
        );
        const rowBasedGroup = getTaskGroups(
          aspirateTasks,
          labwareState,
          false,
          {row: false, column: false},
          {row: 1, column: 1},
        );
        taskGroups = columnBasedGroup.length > rowBasedGroup.length ? rowBasedGroup : columnBasedGroup;
      }

      const sampleTask = jsonCopy(aspirateTasks[0]);
      const destinationTask = jsonCopy(dispenseTasks[0]);
      destinationTask.rowTipCount = dispenseSize.row;
      destinationTask.columnTipCount = dispenseSize.column;

      const newTaskList = [];

      taskGroups.forEach((group) => {
        const newSampleTask = {...sampleTask};

        newSampleTask.row = group.row;
        newSampleTask.column = group.column;
        newSampleTask.rowTipCount = group.rowTipCount;
        newSampleTask.columnTipCount = group.columnTipCount;
        newSampleTask.labwareID = group.labwareId;

        newTaskList.push(newSampleTask);
      });

      dispatch(
        convertPoolingTaskOnTrough(
          newRow,
          newColumn,
          destinationTask,
          newTaskList,
          labware,
          selectedCard,
          isMaintainWellCount,
        ),
      );
      dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition([labwareId]));

      return;
    }

    dispenseTasks = jsonCopy(filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE}));

    dispenseTasks.forEach((task) => {
      editedTasks.push({
        ...task,
        column: newColumn,
        row: newRow,
        labwareID: labwareId,
      });
    });

    batch(() => {
      dispatch(editPoolingTask(editedTasks));
    });
  };

export const addAndRemovePoolingTask =
  (aspirateTasks, dispenseTasks, rowColumnListToAdd, rowColumnListToRemove) => (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const sampleTask = aspirateTasks[0];

    if (rowColumnListToAdd.length) {
      dispatch(
        addPoolingAspirateDispenseTasks(
          sampleTask.labwareID,
          rowColumnListToAdd,
          sampleTask.rowTipCount,
          sampleTask.columnTipCount,
          dispenseTasks[0],
        ),
      );
    }

    const taskIdsToRemove = [];

    rowColumnListToRemove.forEach((item) => {
      const taskToRemove = aspirateTasks.filter(
        (task) => task.row.charCodeAt(0) - 64 === item.row && task.column === item.column,
      );

      if (taskToRemove[0]?.ID) taskIdsToRemove.push(taskToRemove[0].ID);
    });

    if (taskIdsToRemove.length) {
      dispatch(removeExcesPoolingTask(taskIdsToRemove, true));
    }
  };

export const savePoolingStateBeforeEdit =
  (originState = {}) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();

    dispatch(temporaryStateAction.addTemporaryState('poolingStateBeforeEdit', {cardState, labwareState, originState}));
  };

export const restorePoolingStateAfterCancelEdit = () => (dispatch, getState) => {
  const {temporaryState} = getState();
  const {cardState, labwareState} = temporaryState.poolingStateBeforeEdit;
  const {cards, tasks} = cardState;

  dispatch(temporaryStateAction.removeTemporaryState('poolingStateBeforeEdit'));
  dispatch(cardStateAction.restoreCardState(cards));
  dispatch(cardStateAction.restoreCardTaskState(tasks));
  dispatch(labwareStateAction.restoreLabwareState(labwareState));
};

export const clearPoolingStateBeforeEdit = () => (dispatch) => {
  dispatch(temporaryStateAction.removeTemporaryState('poolingStateBeforeEdit'));
};

export const getPoolingStateBeforeEdit = () => (dispatch, getState) => {
  const {temporaryState} = getState();

  return temporaryState.poolingStateBeforeEdit;
};

export const onPoolingReuseTipChange = (isReuseTips) => (dispatch, getState) => {
  const {cardState, labwareState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  const {ID: selectedCardId} = selectedCard;
  const aspirateDispenseTasks = [];

  let taskListToRearrange = [];
  let taskIdsToRemove = [];

  const dispenseTasks = dispatch(getTaskDispensePooling());
  const dispenseLabware = find(labwareState, {LabwareID: dispenseTasks[0].labwareID});

  dispatch(cardPoolingStateAction.setPoolingReuseTip(selectedCardId, isReuseTips));

  if (isReuseTips) {
    const result = extractAdditionalPickEjectTipsAndAdjustTask(selectedCard, tasks);
    const {pickEjectTipTasks, adjustedRestOfTasks} = result;

    const ejectTip = {
      ...adjustedRestOfTasks.splice(-1)[0],
      changeTip: false,
    };

    taskIdsToRemove = pickEjectTipTasks.map((e) => e.ID);
    taskListToRearrange = [...adjustedRestOfTasks, ejectTip];
  }

  if (dispenseLabware.isTrough && !isReuseTips) {
    const aspirateTasks = dispatch(getTaskAspiratePooling());
    taskIdsToRemove = [...selectedCard.tasks];

    const {poolingHelperState} = selectedCard;
    const {allowedTips} = poolingHelperState;

    const columnTipCount =
      dispenseTasks[0].columnTipCount < allowedTips.column ? dispenseTasks[0].columnTipCount : allowedTips.column;
    const rowTipCount = dispenseTasks[0].rowTipCount < allowedTips.row ? dispenseTasks[0].rowTipCount : allowedTips.row;

    const forceToSplit = {
      row: false,
      column: false,
    };
    const splitBeforeGrouping = {row: 1, column: 1};
    const maxSizePerGroup = {
      row: isReuseTips || dispenseLabware.rowCount > 1 ? rowTipCount : null,
      column: isReuseTips || dispenseLabware.colCount > 1 ? columnTipCount : null,
    };

    const columnBase = isReuseTips ? dispenseLabware.colCount !== 1 : dispenseLabware.colCount === 1;

    const taskGroup = getTaskGroups(
      aspirateTasks,
      labwareState,
      columnBase,
      forceToSplit,
      splitBeforeGrouping,
      maxSizePerGroup,
    );

    const {volume} = dispenseTasks[0];
    const {mixingVolume} = dispenseTasks[0];

    taskGroup.forEach((task) => {
      // add aspirate tasks
      aspirateDispenseTasks.push({
        ...task,
        type: TaskType.ASPIRATE,
        row: convertRowToNumeric(task.row),
        volume,
        mixingVolume,
      });
      // add dispense tasks
      aspirateDispenseTasks.push({
        ...dispenseTasks[0],
        row: convertRowToNumeric(dispenseTasks[0].row),
        labwareId: dispenseTasks[0].labwareID,
        rowTipCount: task.rowTipCount,
        columnTipCount: task.columnTipCount,
      });
    });
  } else if (!isReuseTips) {
    // filter and sort pooling task
    selectedCard.tasks.forEach((id) => {
      const task = tasks[id];
      taskIdsToRemove.push(task.ID);

      if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
        aspirateDispenseTasks.push({
          type: task.type,
          labwareId: task.labwareID,
          row: task.row.charCodeAt(0) - 64,
          column: task.column,
          rowTipCount: task.rowTipCount,
          columnTipCount: task.columnTipCount,
          isIgnored: dispenseTasks[0].isIgnored ?? false,
        });
      }
    });
  }

  batch(() => {
    if (aspirateDispenseTasks.length) {
      dispatch(batchAddPoolingTaskByCardId(selectedCardId, aspirateDispenseTasks, true, true));
    }

    if (taskListToRearrange) {
      dispatch(cardStateAction.batchRestoreCardTaskStateById(taskListToRearrange));
    }

    dispatch(cardStateAction.removeCardTasks(selectedCardId, taskIdsToRemove));
    dispatch(restoreTipCountForSingleCard(selectedCardId));
  });
};

export const generateAspirateRowColumnsListFromSwipingInfo =
  (swipingInfo, forceSkip, dispenseTasks, noSkip = false, is384 = false) =>
  (dispatch, getState) => {
    const {labwareState} = getState();
    const sampleTask = dispenseTasks[0];
    const dispenseLabware = find(labwareState, {LabwareID: sampleTask.labwareID});
    const {rowCount, colCount} = dispenseLabware;

    let newRowTipCount = swipingInfo.tipCount.row;
    if (newRowTipCount <= 1) {
      newRowTipCount = is384 ? Math.ceil(swipingInfo.swipingSize.row / 2) : swipingInfo.swipingSize.row;
      if (newRowTipCount > 1 && rowCount > 1) {
        newRowTipCount = 1;
      }
    }

    let newColTipCount = swipingInfo.tipCount.column;
    if (newColTipCount <= 1) {
      newColTipCount = is384 ? Math.ceil(swipingInfo.swipingSize.column / 2) : swipingInfo.swipingSize.column;
      if (newColTipCount > 1 && colCount > 1) {
        newColTipCount = 1;
      }
    }

    swipingInfo.tipCount = {row: newRowTipCount, column: newColTipCount};
    const rowColumnList = generateRowColumnsListFromSwipingInfo(swipingInfo, forceSkip, noSkip, swipingInfo.tipCount);

    // normalize
    rowColumnList.startingRowColumnList.forEach((task) => {
      task.row =
        swipingInfo.swipingDirectionX === SWIPING_DIRECTION_X.RIGHT && newRowTipCount > 1
          ? task.row - swipingInfo.swipingSize.row + 1
          : task.row;
      task.column =
        swipingInfo.swipingDirectionY === SWIPING_DIRECTION_Y.UP && newColTipCount > 1
          ? task.column - swipingInfo.swipingSize.column + 1
          : task.column;
    });

    return rowColumnList;
  };

export const resizePoolingTask = (oldTSLayout, newTSLayout) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard || selectedCard.type !== CardType.POOLING) {
    return;
  }

  const {tasks: selectedCardTasks} = selectedCard;

  const taskPickTip = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.PICK_TIP});
  const taskEjectTip = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.EJECT_TIP});
  const tasksAspirate = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
  const tasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});

  const oldOneDmnsnLayout = find(oldTSLayout, {type: TransformSelectionType.ONE_DIMENSION});
  const oldTwoDmnsnLayout = find(oldTSLayout, {type: TransformSelectionType.TWO_DIMENSION});
  const newOneDmnsnLayout = find(newTSLayout, {type: TransformSelectionType.ONE_DIMENSION});
  const newTwoDmnsnLayout = find(newTSLayout, {type: TransformSelectionType.TWO_DIMENSION});

  const taskList = [];
  const taskListToAdd = [];
  const taskListToRemove = [];

  // add/remove task
  if (newTSLayout.isAddOrRemoved) {
    const {start: oldStart, size: oldSize} = oldTwoDmnsnLayout;
    const {start: newStart, size: newSize, labware} = newTwoDmnsnLayout;
    const {labware: oneDmnsLabware} = newOneDmnsnLayout;

    const isLabware384 = labware.rowCount * labware.colCount === LabwareWellCount.LABWARE_384;
    const isPoolingSingleWellDestination = newOneDmnsnLayout.size.row === 1 && newOneDmnsnLayout.size.column === 1;
    const oneDmnsWellCount = oneDmnsLabware.colCount * oneDmnsLabware.rowCount;

    const rowDiff = Math.abs(newSize.row - oldSize.row);
    const columnDiff = Math.abs(newSize.column - oldSize.column);

    /* eslint-disable no-nested-ternary */
    const maxRow = isPoolingSingleWellDestination
      ? oneDmnsWellCount === LabwareWellCount.LABWARE_12
        ? 1
        : newSize.row
      : 1;
    const maxColumn = isPoolingSingleWellDestination
      ? oneDmnsWellCount === LabwareWellCount.LABWARE_8
        ? 1
        : newSize.column
      : 1;

    if (oldStart.row !== newStart.row) {
      // resize with handle e
      if (oldStart.row > newStart.row) {
        // increment
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && ((i % 2 === 1 && rowDiff % 2 === 0) || (i % 2 === 0 && rowDiff % 2 === 1))) {
            continue;
          }

          const newRow = newStart.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }
            const newColumn = oldStart.column + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldStart.row < newStart.row) {
        // decrement
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 1) {
            continue;
          }

          const newRow = oldStart.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldStart.column !== newStart.column) {
      // resize with handle n
      if (oldStart.column > newStart.column) {
        // increment
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && ((i % 2 === 1 && columnDiff % 2 === 0) || (i % 2 === 0 && columnDiff % 2 === 1))) {
            continue;
          }
          const newColumn = newStart.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldStart.column < newStart.column) {
        // decrement
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 1) {
            continue;
          }

          const newColumn = oldStart.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldSize.row !== newSize.row) {
      // resize with handle w
      if (oldSize.row < newSize.row) {
        // increment
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newRow = oldStart.row + oldSize.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldSize.row > newSize.row) {
        // decrement
        for (let i = 1; i <= rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newRow = oldStart.row + oldSize.row - i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldSize.column !== newSize.column) {
      // resize with handle s
      if (oldSize.column < newSize.column) {
        // increment
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newColumn = oldStart.column + oldSize.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldSize.column > newSize.column) {
        // decrement
        for (let i = 1; i <= columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }
          const newColumn = oldStart.column + oldSize.column - i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    }

    dispatch(addAndRemovePoolingTask(tasksAspirate, tasksDispense, taskListToAdd, taskListToRemove));
    return;
  }

  // increase/decrease tip count
  const diffRowDispense = newOneDmnsnLayout.size.row - oldOneDmnsnLayout.size.row;
  const diffColumnDispense = newOneDmnsnLayout.size.column - oldOneDmnsnLayout.size.column;

  const isNewStartDispense =
    newOneDmnsnLayout.start.row !== oldOneDmnsnLayout.start.row ||
    newOneDmnsnLayout.start.column !== oldOneDmnsnLayout.start.column;

  const {labware: dispenseLabware} = newOneDmnsnLayout;

  tasksDispense.forEach((task) => {
    let newRow = convertRowToNumeric(task.row) - diffRowDispense;
    let newColumn = task.column - diffColumnDispense;

    // normalize newRow and newColumn
    newRow = newRow < 1 || dispenseLabware.rowCount === 1 ? newOneDmnsnLayout.start.row : newRow;
    newColumn = newColumn < 1 || dispenseLabware.colCount === 1 ? newOneDmnsnLayout.start.column : newColumn;

    taskList.push({
      ...task,
      row: isNewStartDispense ? newRow : convertRowToNumeric(task.row),
      column: isNewStartDispense ? newColumn : task.column,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  const {labware: labwareAspirate} = newTwoDmnsnLayout;
  const isAspirateLabware384 = labwareAspirate.rowCount * labwareAspirate.colCount === LabwareWellCount.LABWARE_384;

  let diffRowAspirate = newTwoDmnsnLayout.size.row - oldTwoDmnsnLayout.size.row;
  let diffColumnAspirate = newTwoDmnsnLayout.size.column - oldTwoDmnsnLayout.size.column;

  if (isAspirateLabware384) {
    diffRowAspirate -= diffRowAspirate % 2 === 1 ? 1 : 0;
    diffColumnAspirate -= diffColumnAspirate % 2 === 1 ? 1 : 0;
  }

  const isNewStartAspirate =
    newTwoDmnsnLayout.start.row !== oldTwoDmnsnLayout.start.row ||
    newTwoDmnsnLayout.start.column !== oldTwoDmnsnLayout.start.column;

  tasksAspirate.forEach((task) => {
    let newRow = convertRowToNumeric(task.row) - diffRowAspirate;
    let newColumn = task.column - diffColumnAspirate;

    // normalize newRow and newColumn
    newRow = newRow < 1 || labwareAspirate.rowCount === 1 ? newTwoDmnsnLayout.start.row : newRow;
    newColumn = newColumn < 1 || labwareAspirate.colCount === 1 ? newTwoDmnsnLayout.start.column : newColumn;

    taskList.push({
      ...task,
      row: isNewStartAspirate ? newRow : convertRowToNumeric(task.row),
      column: isNewStartAspirate ? newColumn : task.column,
      rowTipCount: newTwoDmnsnLayout.tipCount.row,
      columnTipCount: newTwoDmnsnLayout.tipCount.column,
    });
  });

  // syncing tip count in pick tip task
  taskPickTip.forEach((task) => {
    taskList.push({
      ...task,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  // syncing tip count in eject tip task
  taskEjectTip.forEach((task) => {
    taskList.push({
      ...task,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  dispatch(editPoolingTask(taskList, true));
};

export const setPoolingHelperState = (poolingHelperState) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const selectedCard = find(cards, {isSelected: true});
  if (selectedCard) {
    const newCardState = {
      ...selectedCard,
      poolingHelperState,
    };

    dispatch(cardStateAction.restoreCardStateById(newCardState));
  }
};

export const getPoolingDispenseWellSize = (task, labware) => ({
  row: task.rowTipCount > labware?.rowCount ? labware?.rowCount : task.rowTipCount,
  column: task.columnTipCount > labware?.colCount ? labware?.colCount : task.columnTipCount,
});

export const getPoolingDestinationAllowedTip = (poolingTipsPattern, aspirateTasks) => (dispatch, getState) => {
  const {labwareState} = getState();

  const taskDispenses = dispatch(getTaskDispensePooling());
  let taskAspirates;
  if (aspirateTasks) {
    taskAspirates = aspirateTasks;
  } else {
    taskAspirates = dispatch(getTaskAspiratePooling());
  }

  if (taskDispenses.length && !aspirateTasks) {
    const taskDispense = taskDispenses[0];
    let taskGroups = [];
    let groupedByColumn = true;
    if (taskDispense.rowTipCount > 1) {
      taskGroups = getTaskGroups(taskAspirates, labwareState, false);
      groupedByColumn = false;
    } else {
      taskGroups = getTaskGroups(taskAspirates, labwareState);
    }
    const columns = selectDistinct(taskGroups.map((task) => task.columnTipCount));
    const rows = selectDistinct(taskGroups.map((task) => task.rowTipCount));

    let allowedRow = rows.length > 1 ? taskDispense.rowTipCount : rows[0];
    let allowedColumn = columns.length > 1 ? taskDispense.columnTipCount : columns[0];
    rows.forEach((tip) => {
      if (tip % taskDispense.rowTipCount !== 0) {
        allowedRow = 1;
      }
    });
    columns.forEach((tip) => {
      if (tip % taskDispense.columnTipCount !== 0) {
        allowedColumn = 1;
      }
    });
    return {
      allowedTips: {row: allowedRow, column: allowedColumn},
      taskGroups,
      groupedByColumn,
    };
  }
  let maxSizePerGroup = {row: null, column: null};

  if (poolingTipsPattern) {
    maxSizePerGroup = poolingTipsPattern;
  }

  const taskGroupsByColumn = getTaskGroups(taskAspirates, labwareState, true, null, null, maxSizePerGroup);
  const taskGroupsByRow = getTaskGroups(taskAspirates, labwareState, false, null, null, maxSizePerGroup);

  const groupedByColumn = taskGroupsByColumn.length <= taskGroupsByRow.length;

  let taskGroups = taskGroupsByColumn.length < taskGroupsByRow.length ? taskGroupsByColumn : taskGroupsByRow;
  const filteredTaskGroupColumns = taskGroups.filter((task) => {
    const labware = find(labwareState, {LabwareID: task.labwareId});
    return !labware.isTrough || (labware.isTrough && labware.colCount > 1);
  });
  const filteredTaskGroupRows = taskGroups.filter((task) => {
    const labware = find(labwareState, {LabwareID: task.labwareId});
    return !labware.isTrough || (labware.isTrough && labware.rowCount > 1);
  });

  const columns = selectDistinct(filteredTaskGroupColumns.map((task) => task.columnTipCount));
  const rows = selectDistinct(filteredTaskGroupRows.map((task) => task.rowTipCount));

  const allowedRowTipCount = rows.length ? getGCDFromArray(rows) : 8;
  const allowedColumnTipCount = columns.length ? getGCDFromArray(columns) : 12;

  const isRowGroupExceed = taskGroups.find((task) => task.rowTipCount > allowedRowTipCount);
  const isRowColumnGroupExceed = taskGroups.find((task) => task.columnTipCount > allowedColumnTipCount);

  if (isRowGroupExceed && !groupedByColumn) {
    taskGroups = getTaskGroups(
      taskAspirates,
      labwareState,
      groupedByColumn,
      {row: false, column: false},
      {row: false, column: false},
      {row: allowedRowTipCount, column: allowedColumnTipCount},
    );
  } else if (isRowColumnGroupExceed && groupedByColumn) {
    taskGroups = getTaskGroups(
      taskAspirates,
      labwareState,
      groupedByColumn,
      {row: false, column: false},
      {row: false, column: false},
      {row: allowedRowTipCount, column: allowedColumnTipCount},
    );
  }

  return {
    allowedTips: {row: allowedRowTipCount, column: allowedColumnTipCount},
    taskGroups,
    groupedByColumn,
  };
};

export const batchEditPoolingAspirateTasks =
  (
    labwareId,
    // eslint-disable-next-line default-param-last
    rowColumnList = [],
    // eslint-disable-next-line default-param-last
    taskIdToRemove = [],
    tipCount,
    skipSaveState = false,
  ) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards} = cardState;
    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    batch(() => {
      const dispenseTasks = dispatch(getTaskDispensePooling());
      const dispenseLabware = find(labwareState, {LabwareID: dispenseTasks[0]?.labwareID});
      const dispenseSize = getPoolingDispenseWellSize(dispenseTasks[0], dispenseLabware);

      if (!dispenseLabware.isTrough) {
        dispatch(
          addPoolingAspirateDispenseTasks(
            labwareId,
            rowColumnList,
            dispenseSize.row,
            dispenseSize.column,
            dispenseTasks[0],
          ),
        );
        // if edit, remove dispense task and pick/eject tip is not reuse tip
        if (taskIdToRemove.length) {
          dispatch(removeExcesPoolingTask(taskIdToRemove));
        }
      } else {
        let aspirateTasks = jsonCopy(dispatch(getTaskAspiratePooling(labwareId)));
        const aspirateTaskIds = aspirateTasks.map((t) => t.ID);
        aspirateTasks = aspirateTasks.filter((task) => !taskIdToRemove.includes(task.ID));

        rowColumnList.forEach((t) => {
          aspirateTasks.push({
            row: convertRowToAlphabet(t.row),
            column: t.column,
            rowTipCount: tipCount.row,
            columnTipCount: tipCount.column,
            labwareID: labwareId,
          });
        });
        let maxSizePerGroup = {row: null, column: null};
        const isReuseTip = typeof selectedCard?.reuseTips === 'boolean' ? selectedCard?.reuseTips : true;

        if (dispenseLabware?.rowCount === 1 && dispenseLabware?.colCount > 1) {
          maxSizePerGroup = {row: isReuseTip ? dispenseSize.row : null, column: dispenseSize.column};
        } else if (dispenseLabware?.rowCount > 1 && dispenseLabware?.colCount === 1) {
          maxSizePerGroup = {row: dispenseSize.row, column: isReuseTip ? dispenseSize.column : null};
        } else if (dispenseLabware?.rowCount === 1 && dispenseLabware?.colCount === 1 && isReuseTip) {
          const result = dispatch(getPoolingDestinationAllowedTip(null, aspirateTasks));
          const {allowedTips} = result;
          maxSizePerGroup = {row: allowedTips.row, column: allowedTips.column};
        }

        const taskGroupsByColumn = getTaskGroups(
          aspirateTasks,
          labwareState,
          true,
          null,
          {row: 1, column: 1},
          maxSizePerGroup,
        );
        const taskGroupsByRow = getTaskGroups(
          aspirateTasks,
          labwareState,
          false,
          null,
          {row: 1, column: 1},
          maxSizePerGroup,
        );

        let currentTaskGroups =
          taskGroupsByColumn.length < taskGroupsByRow.length ? taskGroupsByColumn : taskGroupsByRow;
        currentTaskGroups = selectDistinct(currentTaskGroups);

        const taskGroupByTipCount = getTaskGroupByTipCountAndLabware(currentTaskGroups, labwareState);

        if (Object.keys(taskGroupByTipCount).length) {
          Object.values(taskGroupByTipCount).forEach((group) => {
            dispatch(
              addPoolingAspirateDispenseTasks(
                group.labwareID,
                group.startingRowColumnList,
                group.tipCount.row,
                group.tipCount.column,
                dispenseTasks[0],
              ),
            );
          });
        }
        dispatch(removeExcesPoolingTask(selectDistinct([...taskIdToRemove, ...aspirateTaskIds])));
      }

      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const duplicatePoolingTaskOnPlateSwap =
  (card, tasks, labwareID, rowShift = 0, columnShift = 0, taskToShiftIDs = []) =>
  (dispatch, getState) => {
    const shiftWell = (value, shifter, ID) => (!taskToShiftIDs.includes(ID) ? value : value * 2 - 1 + shifter);

    const destinationPooling = find(tasks, {ID: card.tasks, type: TaskType.DISPENSE});
    if (destinationPooling.labwareID === labwareID) {
      // duplicate a whole card
      dispatch(cardStateAction.setAllCardIsSelected(false));
      dispatch(cardStateAction.setAllTaskIsSelected(false));

      const {cardState} = getState();
      const {cards} = cardState;

      const [cardId] = generateCardIDAndTaskIDAndNumber(cards, tasks, CardType.POOLING);

      dispatch(createPoolingCard(cardId));
      const existTasks = filterToArray(
        tasks,
        (task) => card.tasks.includes(task.ID) && (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE),
      );

      const taskConfigList = existTasks.map((task) => ({
        ...task,
        labwareId: task.labwareID,
        row: shiftWell(convertRowToNumeric(task.row), rowShift, task.ID),
        column: shiftWell(task.column, columnShift, task.ID),
      }));
      dispatch(batchAddPoolingTaskByCardId(cardId, taskConfigList, true, false, true));
    } else {
      const existTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      const taskConfigList = [];
      existTasks.forEach((task) => {
        taskConfigList.push({
          ...task,
          labwareId: task.labwareID,
          row: shiftWell(convertRowToNumeric(task.row), rowShift, task.ID),
          column: shiftWell(task.column, columnShift, task.ID),
        });
        taskConfigList.push({
          ...destinationPooling,
          labwareId: destinationPooling.labwareID,
          columnTipCount: task.columnTipCount,
          rowTipCount: task.rowTipCount,
          row: convertRowToNumeric(destinationPooling.row),
        });
      });

      dispatch(batchAddPoolingTaskByCardId(card.ID, taskConfigList, true, false, true));
    }
  };

export const handlePlateSwapDuplicateInPooling =
  (labwareID, choosenQuadrant, quadrantShifter) => (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const poolingCards = filterToArray(cards, {type: CardType.POOLING});
    poolingCards.forEach((card) => {
      const existPoolingTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      if (existPoolingTasks.length) {
        choosenQuadrant.forEach((q) => {
          dispatch(
            duplicatePoolingTaskOnPlateSwap(
              card,
              tasks,
              labwareID,
              quadrantShifter[q].row,
              quadrantShifter[q].column,
              existPoolingTasks.map((task) => task.ID),
            ),
          );
        });
      }
    });
  };

export const handlePlateSwapMergeInPooling =
  (
    labwareID,
    // eslint-disable-next-line default-param-last
    shiftWell = () => {},
    defaultQuadrant,
  ) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    let processedIds = [];

    const poolingCards = filterToArray(cards, {type: CardType.POOLING});
    poolingCards.forEach((card) => {
      const existPoolingTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      const taskIdsToRemove = [];
      const movedTasks = [];

      processedIds = [...processedIds, ...card.tasks];
      let removeCard = false;

      existPoolingTasks.forEach((task) => {
        let newRow = convertRowToNumeric(task.row);
        let newColumn = task.column;
        const quadrant = getQuadrant(newRow, newColumn);
        if (defaultQuadrant && quadrant === defaultQuadrant) {
          [newRow, newColumn] = shiftWell(newRow, newColumn);
          movedTasks.push({
            ...task,
            row: convertRowToAlphabet(newRow),
            column: newColumn,
          });
        } else if (task.type === TaskType.ASPIRATE) {
          taskIdsToRemove.push(task.ID);
        } else {
          removeCard = true;
        }
      });

      if (removeCard) {
        dispatch(cardStateAction.removeCard(card.ID));
      } else {
        dispatch(cardStateAction.batchRestoreCardTaskStateById(movedTasks));
        dispatch(removeExcesPoolingTask(taskIdsToRemove, true, card.ID, true));
      }
    });

    return processedIds;
  };

export const closePoolingVolumeModal =
  (protocolPoolingTaskEditStep, protocolPoolingTaskStep, volume, currentVolume, isEdit, skipAddUndoItem) =>
  (dispatch) => {
    batch(() => {});
    dispatch(protocolEditorStateAction.setVolumeModalOpen(false));

    if (protocolPoolingTaskEditStep === 1) {
      dispatch(protocolEditorStateAction.setPoolingTaskEditStep(3));
    } else {
      dispatch(protocolEditorStateAction.setPoolingTaskEditStep(null));
    }

    const parsedVolume = Number.isNaN(parseFloat(volume)) ? 0 : parseFloat(volume);
    const parsedCurrentVolume = Number.isNaN(parseFloat(currentVolume)) ? 0 : parseFloat(currentVolume);

    if (isEdit && !skipAddUndoItem && (!parsedVolume || parsedCurrentVolume === parsedVolume)) {
      dispatch(undoRedoBusinessAction.globalAddUndoItem());
      dispatch(clearPoolingStateBeforeEdit());
      return null;
    }

    if (!parsedVolume) {
      dispatch(poolingBackHandler(protocolPoolingTaskStep ?? protocolPoolingTaskEditStep));

      if (!isEdit) {
        dispatch(protocolEditorStateAction.setPoolingTaskStep(1));
        // resetAllPoolingTasksRef();
      } else {
        dispatch(clearPoolingStateBeforeEdit());
      }

      return null;
    }

    if (isEdit) {
      const editTaskSuccessMessage = intl.formatMessage({id: 'protocolEditor.pooling.editTask.successMessage'});

      dispatch(showSnackbar(Feedback.SUCCESS, editTaskSuccessMessage));
      dispatch(clearPoolingStateBeforeEdit());
    }

    dispatch(setPoolingVolume(parsedVolume, true));
    return parsedVolume;
  };

export const onFinishPoolingWizard = (cardId) => (dispatch) => {
  dispatch(checkInvalidPoolingCard(cardId));
  dispatch(protocolEditorStateAction.setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));
  dispatch(protocolEditorStateAction.setPoolingTaskStep(null));
  dispatch(protocolEditorStateAction.setPoolingTaskEditStep(null));
  dispatch(restoreTipCountForCards([cardId]));
};
